// Bewegung im ganzen Theme: Vorhang (Intro und Seitenwechsel), Parallax, Bühne mit Buchstaben,
// gestapelte Karten, Zeilen- und Wort-Reveal, Menü aus zwei Hälften, Kopfzeile.
// Ohne Bibliothek für alles, was am Scrollen hängt (ein requestAnimationFrame pro Bild).
// Bei "Bewegung reduzieren" (System oder Knopf in der Fußzeile) bleibt alles ruhig und vollständig sichtbar.
(() => {
  const html = document.documentElement;
  const reduced = () => (window.siteReducedMotion ? window.siteReducedMotion() : window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const motion = () => html.classList.contains('motion');
  const designMode = html.classList.contains('design-mode') || Boolean(window.Shopify && window.Shopify.designMode);
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const vh = () => window.innerHeight;

  /* ---------- Text zerlegen: Zeilen, Wörter, Buchstaben ---------- */
  const wrapWords = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      if (!node.textContent.trim()) return;
      const frag = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(' '));
          return;
        }
        const word = document.createElement('span');
        word.className = 'word';
        const inner = document.createElement('span');
        inner.className = 'word__inner';
        inner.textContent = part;
        word.appendChild(inner);
        frag.appendChild(word);
      });
      node.replaceWith(frag);
    });
  };

  const splitWords = (el) => {
    if (el.dataset.original === undefined) el.dataset.original = el.innerHTML;
    wrapWords(el);
    // Kinder-Elemente (z. B. kleines Bild) zählen wie ein Wort
    let i = 0;
    el.querySelectorAll('.word, .wipe').forEach((w) => w.style.setProperty('--i', i++));
    el.classList.add('is-split');
  };

  // Absätze in Zeilen: Wörter messen, nach Zeilenanfang gruppieren
  const splitLines = (el) => {
    if (el.dataset.original === undefined) el.dataset.original = el.innerHTML;
    else el.innerHTML = el.dataset.original;
    const blocks = el.querySelectorAll('p, li, h3, h4').length ? el.querySelectorAll('p, li, h3, h4') : [el];
    let index = 0;
    blocks.forEach((block) => {
      wrapWords(block);
      const words = Array.from(block.querySelectorAll('.word'));
      if (!words.length) return;
      const lines = [];
      let top = null;
      words.forEach((w) => {
        const t = Math.round(w.offsetTop);
        if (top === null || Math.abs(t - top) > 4) {
          lines.push([]);
          top = t;
        }
        lines[lines.length - 1].push(w.textContent);
      });
      block.innerHTML = lines
        .map((words) => `<span class="line"><span class="line__inner" style="--i:${index++}">${words.map(escapeHtml).join(' ')}</span></span>`)
        .join('');
    });
    el.classList.add('is-split');
  };
  const escapeHtml = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  const splitChars = (el) => {
    el.querySelectorAll('.stage__line').forEach((line) => {
      const text = line.textContent;
      line.innerHTML = Array.from(text)
        .map((ch) => `<span class="char" aria-hidden="true">${ch === ' ' ? ' ' : escapeHtml(ch)}</span>`)
        .join('');
    });
  };

  /* ---------- Einblenden beim Hineinscrollen ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              entry.target.querySelectorAll('.wipe').forEach((w) => w.classList.add('is-in'));
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
        )
      : null;
  const reveal = (el) => (revealObserver ? revealObserver.observe(el) : el.classList.add('is-in'));

  /* ---------- Scroll-Schleife ---------- */
  const scrubbers = new Set();
  let ticking = false;
  const tick = () => {
    ticking = false;
    const h = vh();
    scrubbers.forEach((s) => {
      if (!s.active) return;
      const rect = s.el.getBoundingClientRect();
      s.update(rect, h);
    });
  };
  const requestTick = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(tick);
    }
  };
  const activity =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => entries.forEach((e) => {
            const s = e.target.__scrub;
            if (s) s.active = e.isIntersecting;
          }),
          { rootMargin: '25% 0px 25% 0px' }
        )
      : null;
  const addScrub = (el, update) => {
    // Zuerst aktiv, damit der Startzustand sofort stimmt; der Observer schaltet ab, was weit weg ist
    const s = { el, update, active: true };
    el.__scrub = s;
    scrubbers.add(s);
    if (activity) activity.observe(el);
    return s;
  };

  // Bild-Parallax: Bild verschiebt sich um bis zu 15 % der Rahmenhöhe (läuft langsamer als die Seite)
  const initParallax = (frame) => {
    const img = frame.querySelector('.parallax__img');
    if (!img) return;
    const isHero = frame.classList.contains('hero__media');
    addScrub(frame, (rect, h) => {
      if (isHero) {
        // Hero: oben fest, beim Wegscrollen sinkt das Bild langsamer ab
        const p = clamp(-rect.top / rect.height);
        img.style.transform = `translate3d(0, ${(p * 0.13 * rect.height).toFixed(1)}px, 0)`;
        return;
      }
      const p = clamp((h - rect.top) / (h + rect.height));
      img.style.transform = `translate3d(0, ${((p - 0.5) * 0.3 * rect.height).toFixed(1)}px, 0)`;
    });
  };

  // Ebenen: ganze Rahmen steigen unterschiedlich schnell
  const initLayer = (el) => {
    const speed = Number(el.dataset.layer || 0);
    if (!speed) return;
    addScrub(el, (rect, h) => {
      const p = (h - rect.top) / (h + rect.height);
      el.style.transform = `translate3d(0, ${((0.5 - p) * speed * h).toFixed(1)}px, 0)`;
    });
  };

  // Bühne: Buchstaben setzen sich zusammen, Bildspalte wandert, kleine Bilder schweben
  const initStage = (stage) => {
    const title = stage.querySelector('[data-assemble]');
    const column = stage.querySelector('[data-stage-column]');
    const floats = Array.from(stage.querySelectorAll('.stage__float'));
    if (title) splitChars(title);
    const chars = title ? Array.from(title.querySelectorAll('.char')) : [];
    const order = chars.map((_, i) => i).sort(() => Math.random() - 0.5);
    const params = chars.map((_, i) => ({
      start: (order[i] / Math.max(1, chars.length)) * 0.42,
      dir: Math.random() > 0.5 ? 1 : -1,
      spin: Math.random() > 0.5 ? 180 : -180,
    }));
    addScrub(stage, (rect, h) => {
      const total = rect.height - h;
      const p = clamp(-rect.top / Math.max(1, total));
      chars.forEach((c, i) => {
        const { start, dir, spin } = params[i];
        const t = easeOut(clamp((p - start) / 0.24));
        if (t >= 1) {
          if (c.__done) return;
          c.__done = true;
          c.style.transform = 'none';
          c.style.opacity = '1';
          return;
        }
        c.__done = false;
        c.style.opacity = t <= 0 ? '0' : '1';
        c.style.transform = `translate3d(0, ${((1 - t) * dir * 0.45 * h).toFixed(1)}px, 0) rotate(${((1 - t) * spin).toFixed(1)}deg) scale(${t.toFixed(3)})`;
      });
      if (column) {
        const travel = column.offsetHeight + h;
        column.style.transform = `translate3d(0, ${(h - p * travel).toFixed(1)}px, 0)`;
      }
      floats.forEach((f) => {
        const speed = Number(f.dataset.speed || 1);
        f.style.transform = `translate3d(0, ${((0.5 - p) * speed * h).toFixed(1)}px, 0)`;
      });
    });
  };

  // Karten: die überdeckte Karte dunkelt ab, je weiter die nächste darüber liegt
  const initCards = (list) => {
    const cards = Array.from(list.querySelectorAll('[data-card]'));
    cards.forEach((card, i) => {
      const next = cards[i + 1];
      if (!next) return;
      addScrub(next, (rect, h) => {
        const cover = clamp(1 - rect.top / h);
        card.style.setProperty('--cover', cover.toFixed(3));
      });
    });
  };

  /* ---------- Hero ---------- */
  const startHero = () => {
    document.querySelectorAll('[data-hero]').forEach((hero) => {
      if (hero.__started) return;
      hero.__started = true;
      const img = hero.querySelector('.hero__img');
      if (img && window.Motion) window.Motion.animate(img, { scale: [1.08, 1] }, { duration: 1.8, easing: [0.16, 1, 0.3, 1] });
      else if (img) img.style.transform = 'none';
      hero.classList.add('is-drawn');
      setTimeout(() => hero.classList.add('is-filled'), 900);
      setTimeout(() => {
        hero.classList.add('is-in');
        hero.querySelectorAll('[data-hero-lines]').forEach((el) => el.classList.add('is-in'));
      }, 650);
    });
  };

  /* ---------- Vorhang ---------- */
  const curtain = document.querySelector('[data-curtain]');
  const ms = (name, fallback) => parseFloat(getComputedStyle(html).getPropertyValue(name)) || fallback;
  const hideCurtain = () => {
    if (!curtain) return;
    curtain.classList.remove('is-active', 'is-open', 'is-closing', 'is-intro', 'is-drawing', 'is-clearing', 'no-transition');
    html.classList.remove('curtain-closed', 'curtain-intro');
  };

  const openCurtain = (intro) =>
    new Promise((resolve) => {
      if (!curtain || getComputedStyle(curtain).display === 'none') {
        hideCurtain();
        resolve();
        return;
      }
      if (intro) {
        curtain.classList.add('is-intro');
        try {
          sessionStorage.setItem('site-intro', 'gesehen');
        } catch (e) {}
        requestAnimationFrame(() => requestAnimationFrame(() => curtain.classList.add('is-drawing')));
        setTimeout(() => curtain.classList.add('is-clearing'), 1050);
        setTimeout(() => curtain.classList.add('is-open'), 1350);
        setTimeout(resolve, 1350 + 450);
        setTimeout(hideCurtain, 1350 + 1100);
      } else {
        requestAnimationFrame(() => curtain.classList.add('is-open'));
        const open = ms('--curtain-open', 700);
        setTimeout(resolve, open * 0.45);
        setTimeout(hideCurtain, open + 80);
      }
    });

  const leaveWithCurtain = (url) => {
    try {
      sessionStorage.setItem('site-vorhang', 'zu');
    } catch (e) {}
    curtain.classList.add('is-active', 'is-open', 'no-transition');
    curtain.getBoundingClientRect();
    curtain.classList.remove('no-transition');
    curtain.classList.add('is-closing');
    requestAnimationFrame(() => curtain.classList.remove('is-open'));
    setTimeout(() => (window.location.href = url), ms('--curtain-close', 600) + 40);
  };

  const transitionsOn = () => document.body.dataset.curtainTransitions === 'true' && motion() && !designMode && curtain;
  document.addEventListener('click', (event) => {
    if (!transitionsOn() || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.target || link.hasAttribute('download') || link.dataset.noCurtain !== undefined) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol)) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
    if (/\/(checkout|account|cart\/(add|change|clear))/.test(url.pathname)) return;
    event.preventDefault();
    leaveWithCurtain(url.href);
  });
  // Zurück-Knopf: Seite aus dem Cache darf nicht hinter dem Vorhang bleiben
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) hideCurtain();
  });

  /* ---------- Menü aus zwei Hälften ---------- */
  const initMenu = () => {
    const menu = document.querySelector('[data-menu]');
    const openBtn = document.querySelector('[data-menu-open]');
    if (!menu || !openBtn) return;
    const closeBtn = menu.querySelector('[data-menu-close]');
    menu.querySelectorAll('.menu__link').forEach((link, i) => link.style.setProperty('--i', i));
    let lastFocus = null;
    const open = () => {
      lastFocus = document.activeElement;
      menu.hidden = false;
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => requestAnimationFrame(() => menu.classList.add('is-open')));
      // preventScroll: sonst schiebt der Fokus „Pakete“ vor dem Einflug in seine Zeile, es blitzt auf
      setTimeout(() => menu.querySelector('.menu__link, [data-menu-close]')?.focus({ preventScroll: true }), 60);
    };
    const close = () => {
      menu.classList.remove('is-open');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      setTimeout(() => (menu.hidden = true), motion() ? 800 : 0);
      (lastFocus || openBtn).focus();
    };
    openBtn.addEventListener('click', open);
    closeBtn?.addEventListener('click', close);
    menu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
      if (event.key !== 'Tab') return;
      const items = Array.from(menu.querySelectorAll('a[href], button'));
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  /* ---------- Kopfzeile: Farbe nach Untergrund, verstecken beim Runterscrollen ---------- */
  const initHeader = () => {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;
    let lastY = window.scrollY;
    const update = () => {
      const y = window.scrollY;
      const probe = document.elementsFromPoint(window.innerWidth / 2, header.offsetHeight + 2).find((el) => !header.contains(el) && el.closest('[data-tone]'));
      const tone = probe ? probe.closest('[data-tone]').dataset.tone : 'light';
      html.dataset.headerTone = tone;
      header.classList.toggle('is-solid', y > (document.querySelector('[data-hero]') ? vh() * 0.9 : 10));
      if (!document.querySelector('.menu.is-open')) header.classList.toggle('is-hidden', y > vh() && y > lastY + 4);
      if (y < lastY - 4 || y < vh()) header.classList.remove('is-hidden');
      lastY = y;
    };
    addScrub(document.body, update).active = true;
    update();
  };

  /* ---------- Videos der Beispielseiten ---------- */
  const initVideos = (root) => {
    root.querySelectorAll('video[data-autoplay], [data-autoplay-wrap] video').forEach((video) => {
      video.muted = true;
      if (!motion()) {
        video.removeAttribute('autoplay');
        video.pause();
        return;
      }
      if (!('IntersectionObserver' in window)) {
        video.play().catch(() => {});
        return;
      }
      new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? video.play().catch(() => {}) : video.pause()));
      }, { threshold: 0.2 }).observe(video);
    });
  };

  /* ---------- Start ---------- */
  const initSection = (root) => {
    const useMotion = motion();
    root.querySelectorAll('[data-reveal-words]').forEach((el) => {
      if (useMotion) splitWords(el);
      reveal(el);
    });
    root.querySelectorAll('[data-reveal-lines], [data-hero-lines]').forEach((el) => {
      if (useMotion) splitLines(el);
      if (!el.hasAttribute('data-hero-lines')) reveal(el);
    });
    root.querySelectorAll('[data-rule], [data-reveal-fade], [data-draw-text], .wipe').forEach(reveal);
    initVideos(root);
    if (!useMotion) return;
    root.querySelectorAll('[data-parallax]').forEach(initParallax);
    root.querySelectorAll('[data-layer]').forEach(initLayer);
    root.querySelectorAll('[data-stage]').forEach(initStage);
    root.querySelectorAll('[data-cards]').forEach(initCards);
  };

  const start = () => {
    initMenu();
    initHeader();
    initSection(document);
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', () => {
      // Zeilen hängen an der Breite: neu zerlegen
      if (motion()) document.querySelectorAll('[data-reveal-lines].is-split, [data-hero-lines].is-split').forEach((el) => splitLines(el));
      requestTick();
    });
    requestTick();

    const intro = html.classList.contains('curtain-intro') && document.querySelector('[data-hero-intro]');
    const closed = html.classList.contains('curtain-closed');
    if (!motion()) {
      hideCurtain();
      document.querySelectorAll('[data-hero]').forEach((h) => h.classList.add('is-drawn', 'is-filled', 'is-in'));
      return;
    }
    const go = () => openCurtain(Boolean(intro)).then(startHero);
    if (intro || closed) (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()).then(() => setTimeout(go, 60));
    else {
      hideCurtain();
      startHero();
    }
  };

  // Theme-Editor: neu geladene Abschnitte sofort in Endzustand
  document.addEventListener('shopify:section:load', (event) => {
    initSection(event.target);
    event.target.querySelectorAll('[data-hero]').forEach((h) => h.classList.add('is-drawn', 'is-filled', 'is-in'));
    event.target.querySelectorAll('[data-reveal-lines], [data-reveal-words], [data-rule], [data-reveal-fade], .wipe').forEach((el) => el.classList.add('is-in'));
    requestTick();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
